import { useEffect, useCallback, useRef, useState } from 'react'
import { useStore, usePrograms, useProgramsLoading, useProgramQuery, useProgramsUpdateSeq } from '../store/index.js'
import { programsApi, ApiError } from '../lib/api.js'
import type { Program } from '../types/index.js'
import { upcomingOrdering, type UpcomingSortKey } from '../components/programs/upcomingBand.js'

// ─── Hook liste ───────────────────────────────────────────────────────────────

/**
 * Chargements de page 1 en cours, indexés par requête. `useProgramList` est
 * monté par plusieurs composants d'un même écran (la vue et sa liste) : sans ce
 * partage, chacun émettait sa propre requête identique.
 */
const inFlightLoads = new Map<string, Promise<unknown>>()

function sharedLoad<T>(key: string, run: () => Promise<T>): Promise<T> {
  const pending = inFlightLoads.get(key)
  if (pending) return pending as Promise<T>
  const promise = run().finally(() => inFlightLoads.delete(key))
  inFlightLoads.set(key, promise)
  return promise
}

export function useProgramList(channelId: string | null) {
  const programs         = usePrograms()
  const loading          = useProgramsLoading()
  const query            = useProgramQuery()
  const programsUpdateSeq = useProgramsUpdateSeq()
  const total            = useStore(state => state.programsTotal)
  const hasMore          = useStore(state => state.programsHasMore)
  // Sélecteurs unitaires : `useStore()` sans sélecteur abonne au store entier,
  // donc au playhead poussé par le websocket — la liste se re-rendait plusieurs
  // fois par seconde.
  const setPrograms         = useStore(state => state.setPrograms)
  const appendPrograms      = useStore(state => state.appendPrograms)
  const setProgramsLoading  = useStore(state => state.setProgramsLoading)
  const setProgramsError    = useStore(state => state.setProgramsError)

  const [loadingMore, setLoadingMore] = useState(false)
  const loadingMoreRef = useRef(false)
  const loadVersionRef = useRef(0)

  const load = useCallback(async () => {
    if (!channelId) return
    const loadVersion = ++loadVersionRef.current
    const params = { ...query, catalog: 'programs' as const, channelId, page: 1 }
    setProgramsLoading(true)
    setProgramsError(null)
    try {
      // La génération WebSocket fait partie de la clé : une invalidation ne
      // doit jamais réutiliser une requête démarrée avant la mutation.
      const key = JSON.stringify({ ...params, programsUpdateSeq })
      const res = await sharedLoad(key, () => programsApi.list(params))
      if (loadVersion !== loadVersionRef.current) return
      setPrograms(res.data, res.meta.total, res.meta.page)
    } catch (err) {
      if (loadVersion !== loadVersionRef.current) return
      setProgramsError(err instanceof ApiError ? err.message : 'Erreur de chargement')
    } finally {
      if (loadVersion === loadVersionRef.current) setProgramsLoading(false)
    }
  }, [channelId, query, programsUpdateSeq, setPrograms, setProgramsLoading, setProgramsError])

  /**
   * Charge la page suivante et l'ajoute à la liste. L'état de pagination est lu
   * dans le store au moment de l'appel : plusieurs composants montent ce hook,
   * la page courante ne doit donc pas être capturée dans une closure.
   */
  const loadMore = useCallback(async () => {
    if (!channelId || loadingMoreRef.current) return
    const { programsHasMore, programsPage, programsLoading } = useStore.getState()
    if (!programsHasMore || programsLoading) return

    loadingMoreRef.current = true
    setLoadingMore(true)
    try {
      const res = await programsApi.list({ ...query, catalog: 'programs', channelId, page: programsPage + 1 })
      appendPrograms(res.data, res.meta.total, res.meta.page)
    } catch (err) {
      setProgramsError(err instanceof ApiError ? err.message : 'Erreur de chargement')
    } finally {
      loadingMoreRef.current = false
      setLoadingMore(false)
    }
  }, [channelId, query, appendPrograms, setProgramsError])

  useEffect(() => {
    void load()
    return () => { loadVersionRef.current++ }
  }, [load])

  return { programs, loading, reload: load, total, hasMore, loadingMore, loadMore }
}

// ─── Hook « Demain et plus loin » ─────────────────────────────────────────────

const UPCOMING_PAGE_SIZE = 50

/**
 * Fiches prévues **après** la journée d'antenne en cours, sous les mêmes filtres
 * et le même tri que la liste principale — seul le découpage `premiere` change.
 *
 * Chargées en dehors du store : la liste principale y vit déjà, et deux jeux de
 * résultats dans le même tableau s'écraseraient au gré des requêtes. Le total
 * est lu dès le montage, bande repliée, pour que la bande annonce combien de
 * fiches elle cache ; les lignes ne sont affichées qu'une fois dépliée.
 */
export function useUpcomingPrograms(channelId: string | null) {
  const query             = useProgramQuery()
  const programsUpdateSeq = useProgramsUpdateSeq()
  const [programs, setPrograms] = useState<Program[]>([])
  const [total,    setTotal]    = useState(0)
  const [page,     setPage]     = useState(1)
  const [loading,  setLoading]  = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const versionRef = useRef(0)

  // Trié par date, le groupe se charge du plus proche au plus lointain : c'est
  // demain qu'on veut sur la première page (cf. `upcomingOrdering`).
  const { fetchSortDir, reversed } = upcomingOrdering(
    (query.sortBy ?? 'premiereDate') as UpcomingSortKey,
    (query.sortDir ?? 'desc') as 'asc' | 'desc',
  )

  const baseParams = useCallback((nextPage: number) => ({
    ...query,
    catalog:  'programs' as const,
    channelId,
    premiere: 'upcoming' as const,
    sortDir:  fetchSortDir,
    page:     nextPage,
    limit:    UPCOMING_PAGE_SIZE,
  }), [query, channelId, fetchSortDir])

  useEffect(() => {
    if (!channelId) { setPrograms([]); setTotal(0); return }
    const version = ++versionRef.current
    setLoading(true)
    programsApi.list(baseParams(1))
      .then(res => {
        if (version !== versionRef.current) return
        setPrograms(res.data)
        setTotal(res.meta.total)
        setPage(1)
      })
      .catch(() => {
        // La bande n'est qu'un complément : en cas d'échec elle se tait plutôt
        // que de masquer la liste principale derrière une erreur.
        if (version === versionRef.current) { setPrograms([]); setTotal(0) }
      })
      .finally(() => { if (version === versionRef.current) setLoading(false) })
    // `programsUpdateSeq` : une fiche datée ou redatée ailleurs change de côté.
  }, [channelId, baseParams, programsUpdateSeq])

  const hasMore = programs.length < total

  const loadMore = useCallback(async () => {
    if (!channelId || loadingMore || !hasMore) return
    const version = versionRef.current
    setLoadingMore(true)
    try {
      const res = await programsApi.list(baseParams(page + 1))
      if (version !== versionRef.current) return
      setPrograms(previous => {
        const known = new Set(previous.map(program => program.id))
        return [...previous, ...res.data.filter((program: Program) => !known.has(program.id))]
      })
      setTotal(res.meta.total)
      setPage(res.meta.page)
    } finally {
      setLoadingMore(false)
    }
  }, [channelId, loadingMore, hasMore, baseParams, page])

  return { programs, total, loading, hasMore, loadingMore, loadMore, reversed }
}

// ─── Hook détail programme ────────────────────────────────────────────────────

export function useProgramDetailById(programId: string | null) {
  const setProgramDetail        = useStore(state => state.setProgramDetail)
  const setProgramDetailLoading  = useStore(state => state.setProgramDetailLoading)
  const programDetail           = useStore(state => state.programDetail)
  const programDetailLoading    = useStore(state => state.programDetailLoading)

  const load = useCallback(async () => {
    if (!programId) { setProgramDetail(null); return }
    setProgramDetailLoading(true)
    try {
      const program = await programsApi.getById(programId)
      setProgramDetail(program)
    } catch {
      setProgramDetail(null)
    } finally {
      setProgramDetailLoading(false)
    }
  }, [programId, setProgramDetail, setProgramDetailLoading])

  useEffect(() => { load() }, [load])

  return { detail: programDetail, loading: programDetailLoading, reload: load }
}

// ─── Actions programmes ───────────────────────────────────────────────────────

export function useProgramActions() {
  const upsertProgram    = useStore(state => state.upsertProgram)
  const removeProgram    = useStore(state => state.removeProgram)
  const upsertMediaAsset = useStore(state => state.upsertMediaAsset)
  const removeMediaAsset = useStore(state => state.removeMediaAsset)

  const createProgram = useCallback(async (data: Parameters<typeof programsApi.create>[0]) => {
    const program = await programsApi.create(data)
    upsertProgram(program)
    return program
  }, [upsertProgram])

  const updateProgram = useCallback(async (id: string, data: Parameters<typeof programsApi.update>[1]) => {
    const program = await programsApi.update(id, data)
    upsertProgram(program)
    return program
  }, [upsertProgram])

  const archiveProgram = useCallback(async (id: string) => {
    await programsApi.archive(id)
    removeProgram(id)
  }, [removeProgram])

  const restoreProgram = useCallback(async (id: string) => {
    const program = await programsApi.restore(id)
    upsertProgram(program)
    return program
  }, [upsertProgram])

  const permanentDeleteProgram = useCallback(async (id: string) => {
    await programsApi.permanentDelete(id)
    removeProgram(id)
  }, [removeProgram])

  const duplicateProgram = useCallback(async (id: string) => {
    const program = await programsApi.duplicate(id)
    upsertProgram(program)
    return program
  }, [upsertProgram])

  const checkMedia = useCallback(async (assetId: string) => {
    const result = await programsApi.checkMedia(assetId)
    const { programDetail } = useStore.getState()
    if (programDetail) {
      const asset = programDetail.mediaAssets?.find((a: any) => a.id === assetId)
      if (asset) {
        upsertMediaAsset({ ...asset, status: result.status as any, lastError: result.issues.join(' | ') || null as any })
      }
    }
    return result
  }, [upsertMediaAsset])

  const addMedia = useCallback(async (programId: string, data: Parameters<typeof programsApi.addMedia>[1]) => {
    const asset = await programsApi.addMedia(programId, data)
    upsertMediaAsset(asset)
    // Sans durée lisible (FFprobe muet, fichier momentanément absent), le
    // backend n'écrit ni la durée de la fiche ni le réalignement de la grille
    // et le fait en silence : la fiche garde alors la durée théorique alors que
    // le fichier associé fait tout autre chose.
    if (!asset.durationSec) {
      useStore.getState().pushToast(
        'Durée du fichier introuvable : la fiche et la grille n’ont pas été réalignées. Relancez l’analyse du média.',
        'warning',
        12000,
      )
    }
    // Le backend recalcule la durée du programme à partir du média non-proxy.
    // On recharge pour propager la nouvelle durée dans tout le store/UI.
    try {
      const refreshed = await programsApi.getById(programId)
      upsertProgram(refreshed as any)
    } catch { /* best-effort, l'asset est déjà sauvegardé */ }
    return asset
  }, [upsertMediaAsset, upsertProgram])

  // Reclasser un fichier (version longue / courte) déplace la durée de la
  // fiche : on la recharge comme après un rattachement.
  const updateMedia = useCallback(async (assetId: string, data: Parameters<typeof programsApi.updateMedia>[1]) => {
    const asset = await programsApi.updateMedia(assetId, data)
    upsertMediaAsset(asset)
    try {
      const refreshed = await programsApi.getById(asset.programId)
      upsertProgram(refreshed as any)
    } catch { /* best-effort */ }
    return asset
  }, [upsertMediaAsset, upsertProgram])

  const deleteMedia = useCallback(async (assetId: string, programId: string) => {
    await programsApi.deleteMedia(assetId)
    removeMediaAsset(assetId, programId)
    // La durée du programme bascule sur le média suivant — on resynchronise.
    try {
      const refreshed = await programsApi.getById(programId)
      upsertProgram(refreshed as any)
    } catch { /* best-effort */ }
  }, [removeMediaAsset, upsertProgram])

  return { createProgram, updateProgram, archiveProgram, restoreProgram, permanentDeleteProgram, duplicateProgram, checkMedia, addMedia, updateMedia, deleteMedia }
}
